/**
 * User Select — pick one user from the admin directory, then show the pick as
 * a chip with a Clear action.
 *
 * The dropdown itself is the shared UserSearchCombobox; this component owns
 * only what is specific to the admin dialogs: the directory query, the
 * selected-user chip, and the id-shaped callbacks the org/permission dialogs
 * already speak.
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { UserSearchCombobox } from '@/components/user/UserSearchCombobox';
import { useAdminUsers } from '@/hooks/useAdminAnalytics';
import type { UserSearchResult } from '@/api/endpoints/users';

interface UserSelectProps {
  onSelect: (userId: string) => void;
  onClear: () => void;
}

export function UserSelect({ onSelect, onClear }: UserSelectProps) {
  const { t } = useTranslation();
  // `error` is not optional to read: without it a failed request is
  // indistinguishable from an empty directory, and the admin is told "No users
  // found" when the truth is "we could not ask".
  const { data: users, error } = useAdminUsers();
  const [query, setQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const clearButtonRef = useRef<HTMLButtonElement>(null);

  // After a pick, the input is replaced by the selected-user chip and the
  // portalled suggestion button unmounts — React would drop focus to <body>,
  // forcing a keyboard user to Tab back in. Move focus to the Clear button so
  // the form stays navigable. Runs only on the null→user transition.
  useEffect(() => {
    if (selectedUser) clearButtonRef.current?.focus();
  }, [selectedUser]);

  function handleSelect(user: UserSearchResult) {
    setSelectedUser(user);
    setQuery('');
    onSelect(user.id);
  }

  function handleClear() {
    setSelectedUser(null);
    setQuery('');
    onClear();
  }

  function userLabel(u: UserSearchResult): string {
    if (u.name) return u.email ? `${u.name} (${u.email})` : u.name;
    return u.email ?? u.id;
  }

  if (selectedUser) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex-1 text-sm px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-md truncate">
          {userLabel(selectedUser)}
        </div>
        <button
          type="button"
          ref={clearButtonRef}
          onClick={handleClear}
          className="text-xs text-neutral-500 hover:text-neutral-700 px-2 py-2 shrink-0"
          aria-label={t('admin.userSelect.clearFilter', 'Clear user filter')}
        >
          {t('admin.userSelect.clear', 'Clear')}
        </button>
      </div>
    );
  }

  return (
    <UserSearchCombobox
      value={query}
      onChange={setQuery}
      onSelect={handleSelect}
      dataSource={{ type: 'client', users, error: Boolean(error) }}
    />
  );
}
